import {
  ClipboardDocumentIcon,
  EllipsisVerticalIcon,
  XMarkIcon
} from '@heroicons/react/20/solid'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { Sticker } from 'cleverev-types'
import { CheckIcon, TrashIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useParams } from 'react-router'
import { toast } from 'sonner'
import { stickerAPI } from '#/api/sticker'
import { Button } from '#/components/ui/button'
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm
} from '#/components/ui/description-list'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '#/components/ui/dropdown-menu'
import { Input } from '#/components/ui/input'
import { Separator } from '#/components/ui/separator'
import { Heading, Subheading } from '#/components/ui/typography'
import { copy } from '#/lib/copy'
import { formatDateTime2Digit } from '#/lib/datetime'
import { DeleteStickerModal } from '../@modal/delete/page'


export default function StickerPage() {
  const { t, i18n } = useTranslation([ 'stickers', 'general' ])
  const params = useParams() as { organization: string; sticker: string }
  const [ isEditing, setIsEditing ] = useState(false)
  const [ localSticker, setLocalSticker ] = useState<Partial<Sticker>>({})
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState<{ id: string } | null>(null)

  const { data: sticker } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization, 'stickers', params.sticker ],
    queryFn: () => stickerAPI.get(params.organization, params.sticker)
  })

  // Copy sticker id and notify
  function copyStickerId(sticker: Sticker) {
    copy(sticker.id)
    toast.info(t('view_sticker.id_copied'))
  }

  function startEditing() {
    setIsEditing(true)
    if (!sticker) return
    setLocalSticker(sticker)
  }

  function cancelEditing() {
    setIsEditing(false)
    setLocalSticker({})
  }

  function renderField(key: keyof Sticker, fallback = '-') {
    if (!sticker) return

    const value = isEditing
      ? localSticker[key] ?? ''
      : sticker[key] ?? fallback

    if (!isEditing) return <>{value}</>

    return (
      <Input
        className="w-full border px-2 rounded"
        type="text"
        value={value as string}
        onChange={e =>
          setLocalSticker({ ...localSticker, [key]: e.target.value })
        }
      />
    )
  }

  async function handleSave() {
    try {
      await stickerAPI.update(params.sticker, localSticker)
      toast.success(t('general:saved_successfully'))
      setIsEditing(false)
    } catch {
      toast.error(t('general:save_failed'))
    }
  }

  return (
    <div className="flex grow flex-col gap-4 items-start">

      <Outlet />

      {isDeleteModalOpen !== null
      && (<DeleteStickerModal
        id={isDeleteModalOpen.id}
        onClose={() => setIsDeleteModalOpen(null)}
      />)}

      {sticker && (
        <>
          <div className="flex w-full items-end justify-between gap-4">
            <Heading>{`${t('view_sticker.sticker')} ${sticker.id}`}</Heading>
            <div className="flex flex-row gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger>
                  <Button variant="ghost" size="icon">
                    <EllipsisVerticalIcon className="text-tertiary" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => copyStickerId(sticker)}>
                    <ClipboardDocumentIcon className="text-tertiary" />
                    {t('view_sticker.menu.copy_id')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={isEditing ? handleSave : startEditing}>
                    <ClipboardDocumentIcon className="text-tertiary" />
                    {isEditing ? t('general:save') : t('general:edit')}
                  </DropdownMenuItem>
                  {isEditing && (
                    <DropdownMenuItem onClick={cancelEditing}>
                      {t('general:cancel')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() =>
                    setIsDeleteModalOpen({
                      id: sticker.id
                    })}>
                    <TrashIcon className="text-tertiary" />
                    {t('view_sticker.menu.remove')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <Separator />


          <div className="flex w-full items-center justify-between">
            <Subheading>{t('view_sticker.details')}</Subheading>
          </div>

          <DescriptionList className="w-full">
            <DescriptionTerm>
              {t('fields.id')}
            </DescriptionTerm>
            <DescriptionDetails>
              {sticker.id}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.chargerId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {renderField('chargerId')}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.connectionId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {renderField('connectionId')}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.created_at')}
            </DescriptionTerm>
            <DescriptionDetails>
              {formatDateTime2Digit(sticker.createdAt, i18n.language)}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.updated_at')}
            </DescriptionTerm>
            <DescriptionDetails>
              {formatDateTime2Digit(sticker.updatedAt, i18n.language)}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.organizationId')}
            </DescriptionTerm>
            <DescriptionDetails>
              {sticker.organizationId ?? '-'}
            </DescriptionDetails>
            <DescriptionTerm>
              {t('fields.isTemplate')}
            </DescriptionTerm>
            <DescriptionDetails>
              {sticker.isTemplate === true && (
                <CheckIcon className='size-6' />
              )}
              {sticker.isTemplate === false && (
                <XMarkIcon className='size-6' />
              )}
              {sticker.isTemplate === null && '-'}
            </DescriptionDetails>
          </DescriptionList>
        </>
      )}

    </div>
  )
}
